import React, { Component } from 'react';
import { Button } from 'reactstrap';
import { connect } from 'react-redux';
import { LuRefreshCw } from "react-icons/lu";

import ErrorComponent from '../../../../components/error/ErrorComponent';
import LoadingComponent from '../../../../components/loading/LoadingComponent';
import TableView from '../../../../components/tableView/TableView/TableView';
import CardView from '../../../../components/cardView/CardView';
import HeaderComponent from '../../../../components/header/HeaderComponent';
import DatePicker from '../../../../components/forms/DatePicker';

const translator = require('../../../../utils/translator/translator');
const dispatchUtility = require('../../../../utils/domain/dispatchUtility');
const businessDateUtil = require('../../../../utils/dateUtil/businessDateUtil');
const dateUtil = require('../../../../utils/dateUtil/dateUtil');
const constants = require('../../../../utils/domain/constants');
const logger = require('../../../../utils/logger/logger');
const printUtil = require('../../../../utils/print/printUtil');
const viewUtil = require('../../../../utils/view/viewUtil');
const printingTemplateUtils = require('../../../../utils/domain/printingTemplateUtils');
const dailySummaryReportUtils = require('./dailySummaryReportUtils');
const dailySummaryReportDataUtils = require('./dailySummaryReportDataUtils');


class DailySummaryReport extends Component 
{
    constructor(props)
    {
        super(props);

        this.state = {
            currentDate: null,
            currentSales: null,             // Sales records are domain objects and must not be modified
            currentFloatSessions: null,
            showDatePicker: false,
            error: null,
            isLoading: false
        }

        this.datePickerContainerRef = React.createRef(null);
        this.handleDatePickerOutsideClick = this.handleDatePickerOutsideClick.bind(this);
        this.handleDateChange = this.handleDateChange.bind(this);
    }

    updateState(newState)
    {
        if (this.mounted)
        {
            this.setState(newState);
        }
    }

    componentDidMount() 
    {
        this.mounted = true;
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState, snapshot)
    {
        if (prevProps.todaysSalesUpdatedDateTime !== this.props.todaysSalesUpdatedDateTime) 
        {
            // Implies todaySales updated
            if (businessDateUtil.isDateSameAsTodayBusinessDate(this.state.currentDate))
                this.loadData();
        }
    }

    componentWillUnmount() { this.mounted = false }

    onRefreshData = () =>
    {
        this.loadData(true);    // forceReloading = true
    }

    handleDatePickerOutsideClick() 
    {
        this.setState({ showDatePicker: false })
    }

    handleDateChange(newDate)
    {
        if (newDate)
        {
            this.setState({ currentDate: newDate, showDatePicker: false }, () => this.loadData());
        }
    }

    render()
    {
        if (!this.mounted) return null;

        return (
            <div className='h-100 mt-2 px-2 d-flex flex-column'>

                <div>{this.renderToolbar()}</div>
                <div className='h-100' style={{maxHeight: "100%", overflow: "auto"}}>
                    {this.renderReportBody()}
                </div>
                <DatePicker
                    value={this.state.currentDate}
                    innerRef={this.datePickerContainerRef}
                    isOpen={this.state.showDatePicker}
                    onChange={this.handleDateChange}
                    onOutSideClick={this.handleDatePickerOutsideClick}
                />
            </div>
        );
    }

    renderToolbar()
    {
        const { currentDate, isLoading: isDisabled } = this.state;

        const dateCaption =
            businessDateUtil.isDateSameAsTodayBusinessDate(currentDate)
                ? <span className='px-4'>{translator.getLabel("today")}</span>
                : <span>{dateUtil.formatDate(currentDate, "MMM DD, yyyy")}</span>;

        return (
            <HeaderComponent title={this.props.title}>
                <div className='d-flex px-2'>
                    <Button
                        className='ms-auto btn-action px-5'
                        disabled={isDisabled}
                        onClick={() =>
                        {
                            logger.usage("DailySummaryReport.Refresh", "Staff clicked refresh button to refresh sales");
                            this.onRefreshData();
                        }}>
                        <LuRefreshCw />
                    </Button>
                    <Button
                        className='ms-3 btn-action'
                        disabled={isDisabled}
                        innerRef={this.datePickerContainerRef}
                        onClick={() =>
                        {
                            logger.usage("DailySummaryReport.Calendar", "Staff clicked calendar button to change date");
                            this.setState(prev => ({ showDatePicker: !prev.showDatePicker }));
                        }}>
                        {dateCaption}
                    </Button>
                    <Button
                        className='ms-2 btn-action'
                        disabled={isDisabled}
                        onClick={() =>
                        {
                            logger.usage("DailySummaryReport.Print", "Staff clicked print button to print");
                            this.onPrint();
                        }}>
                        Print
                    </Button>
                    <Button
                        className='ms-3 btn-cancel'
                        onClick={() =>
                        {
                            logger.usage("DailySummaryReport.Exit", "Staff clicked exit button to exit");
                            dispatchUtility.dispatchScreenKeyboardAction(null);
                            dispatchUtility.dispatchNavigationPageAction(null);
                        }}>
                        Exit
                    </Button>
                </div>
            </HeaderComponent>
        );
    }

    onPrint()
    {
        const dataTables = this.getDataTables();

        printUtil.printDailySummaryReport({
            reportTitle: this.props.title,
            reportDate: dateUtil.formatDate(this.state.currentDate, "dddd, MMM DD, yyyy"),
            data: {
                tableData: dataTables.map(dataTable => dataTable.renderPayload())
            }
        });

        viewUtil.showSuccessAlert(translator.getLabel("printing_instructions_sent_to_the_printer_successfully"));
    }

    renderReportBody()
    {
        const { isLoading, error } = this.state;

        if (isLoading)
        {
            return (
                <div className='d-flex h-100 justify-content-center align-items-center'>
                    <LoadingComponent waitMessage={translator.getLabel("please_wait_loading_data")} />
                </div>
            );
        }

        if (error != null)
        {
            return <ErrorComponent error={error} />
        }
        return (
            <div className='px-4 py-4 d-flex flex-column'>
                {this.renderReportData()}
            </div>
        );
    }

    renderReportData()
    {
        const templateElements = this.getTemplateElements();
        const dataTables = this.getDataTables(templateElements);

        return (
            <div className='daily-summary-report-data'>
                {
                    dataTables.map((tableData, index) =>
                        <div key={`${tableData.field}-${index}`} className='mb-4'>
                            <CardView title={tableData.title} footNotes={tableData.footNotes}>
                                <TableView tableData={tableData} />
                            </CardView>
                        </div>)
                }
            </div>
        );
    }

    async loadData(forceReloading)
    {
        // forceReloading = true when user chooses to refresh data
        
        try
        {
            const currentDate = this.state.currentDate || businessDateUtil.getTodayBusinessDate();

            this.setState({
                currentDate: currentDate,
                currentSales: null,
                currentFloatSessions: null,
                error: null,
                isLoading: true
            });

            const { currentSales, currentFloatSessions } = await dailySummaryReportDataUtils.loadReportData(currentDate, forceReloading);

            this.updateState({ currentSales, currentFloatSessions });
        }
        catch (error)
        {
            logger.error(`Error occurred while loading data in DailySummaryReport; Error: ${error}`, {}, error);

            this.updateState({ error: error, isLoading: false })
        }
        finally
        {
            this.updateState({ isLoading: false })
        }
    }

    getTemplateElements()
    {
        let parsedJson, elements;

        const printingTemplate = printingTemplateUtils.getPrintingTemplate(constants.printingTemplateTypes.dailySummaryReport);

        if (printingTemplate != null)
        {
            try 
            {
                parsedJson = JSON.parse(printingTemplate.json);
            }
            catch (error)
            {
            }
        }

        if (parsedJson != null)
        {
            elements = parsedJson.elements;
        }

        if (elements == null || elements.length === 0) 
        {
            elements = this.getDefaultTemplateElements();
        }

        return elements;
    }

    getDefaultTemplateElements()
    {
        // Returns all fields
        return Object
            .keys(dailySummaryReportUtils.fieldTypes)
            .map(key => ({
                field: dailySummaryReportUtils.fieldTypes[key],
            }));
    }

    getDataTables(templateElements)
    {
        if (templateElements == null) 
        {
            // return all dataTables
            templateElements = this.getDefaultTemplateElements();
        }

        const dataTables = [];

        const allFieldTypes = dailySummaryReportUtils.fieldTypes;

        const { currentSales, currentFloatSessions } = this.state;

        const doneSales = currentSales.filter(x => !x.isCancelled);
        const cancelledSales = currentSales.filter(x => x.isCancelled);

        templateElements.forEach(templateElement => 
        {
            switch (templateElement.field)
            {
                case allFieldTypes.salesSummary:
                    dataTables.push(dailySummaryReportDataUtils.getSalesSummaryTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.salesByVenueCode:
                    dataTables.push(dailySummaryReportDataUtils.getSalesByVenueCodeTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.salesByDispatchType:
                    dataTables.push(dailySummaryReportDataUtils.getSalesByDispatchTypeTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.salesByPaymentMethod:
                    dataTables.push(dailySummaryReportDataUtils.getSalesByPaymentMethodTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.salesByMenuCategory:
                    dataTables.push(dailySummaryReportDataUtils.getSalesByMenuCategoryTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.salesByCovers:
                    dataTables.push(dailySummaryReportDataUtils.getSalesByCoversTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.saleDiscounts:
                    dataTables.push(dailySummaryReportDataUtils.getSaleDiscountsTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.saleCharges:
                    dataTables.push(dailySummaryReportDataUtils.getSaleChargesTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.saleRefunds:
                    dataTables.push(dailySummaryReportDataUtils.getSaleRefundsTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.saleCancellations:
                    dataTables.push(dailySummaryReportDataUtils.getSaleCancellationTableData(cancelledSales, templateElement));
                    break;

                case allFieldTypes.saleVats:
                    dataTables.push(dailySummaryReportDataUtils.getSaleVatTableData(doneSales, templateElement));
                    break;

                case allFieldTypes.floatSession:

                    const journalTemplateElement = templateElements.find(element => element.field === allFieldTypes.floatSessionJournal);

                    currentFloatSessions.forEach((floatSession, index) =>
                    {
                        dataTables.push(dailySummaryReportDataUtils.getFloatSessionTableData(floatSession, index, doneSales, templateElement));

                        if (journalTemplateElement != null)
                        {
                            // Show floatSessionJournal summary for the loop floatSession
                            dataTables.push(dailySummaryReportDataUtils.getFloatSessionJournalTableData(floatSession, index, doneSales, journalTemplateElement));
                        }
                    });
                    break;

                case allFieldTypes.floatSessionJournal:
                    // Handled by case allFieldTypes.floatSession
                    break;

                default:
                    throw new Error(`${translator.getLabel("error_occurred_in_DailySummaryReport_renderReportData_unknown_fieldType")} '${templateElement}'`);
            }
        });

        return dataTables;
    }
}

const mapStateToProps = state => 
{
    return { todaysSalesUpdatedDateTime: state.todaysSalesUpdatedDateTime }
}

export default connect(mapStateToProps)(DailySummaryReport)